import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getListenOnlyScript,
  listenOnlyAudioSrc,
  type ListenOnlyChoice,
} from "@app/shared";
import { ClipPlayer } from "../audio/ClipPlayer";

type Phase = "partner" | "choose" | "reading" | "done";

/**
 * 入口の利用ログ。失敗しても無視する。
 * この入口は「どこにいても必ず最後まで動く」ことが存在理由なので、計測で止めない。
 * 個人と紐付けないため、送るのは場面と種別だけ。
 */
function logEvent(
  scenarioId: string,
  event: "opened" | "completed" | "to_main"
): void {
  void fetch("/api/listen-only/event", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ scenarioId, event }),
    keepalive: true,
  }).catch(() => {});
}

/**
 * 「今日は聞くだけ」。声を出さずに30秒で終わる入口。
 * 無料枠を消費しないため、セッションAPI（/api/sessions）は呼ばない。
 */
export function ListenOnlyPage({ scenarioId }: { scenarioId: string }) {
  const navigate = useNavigate();
  const script = getListenOnlyScript(scenarioId);

  const playerRef = useRef<ClipPlayer | null>(null);
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState<Phase>("partner");
  const [started, setStarted] = useState(false);
  const [chosen, setChosen] = useState<ListenOnlyChoice[]>([]);

  useEffect(() => () => playerRef.current?.dispose(), []);

  // 入口を開いたこと自体が、この施策の効果指標になる。
  useEffect(() => logEvent(scenarioId, "opened"), [scenarioId]);

  const play = useCallback(async (src: string) => {
    const player = (playerRef.current ??= new ClipPlayer());
    // 音声がまだ無い場合は false が返る。流れは止めない。
    await player.play(src);
  }, []);

  const listen = useCallback(
    async (index: number) => {
      if (!script) return;
      setStarted(true);
      setPhase("partner");
      await play(listenOnlyAudioSrc(scenarioId, script.steps[index].partner.audioId));
      setPhase("choose");
    },
    [play, scenarioId, script]
  );

  const pick = useCallback(
    async (choice: ListenOnlyChoice) => {
      if (!script) return;
      setChosen((prev) => [...prev, choice]);
      setPhase("reading");
      await play(listenOnlyAudioSrc(scenarioId, choice.audioId));

      const next = step + 1;
      if (next < script.steps.length) {
        setStep(next);
        await listen(next);
      } else {
        setPhase("done");
        logEvent(scenarioId, "completed");
      }
    },
    [listen, play, scenarioId, script, step]
  );

  if (!script) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 p-6 text-center">
        <p className="text-sm text-stone-600">この場面の台本はまだありません。</p>
      </div>
    );
  }

  const current = script.steps[step];

  if (phase === "done") {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col p-4">
        <Header step={script.steps.length} total={script.steps.length} />

        <div className="flex flex-1 flex-col justify-center gap-7 px-1">
          <div className="flex flex-col gap-2">
            <p className="text-[19px] font-semibold leading-[1.5] text-stone-800">
              ここまでです
            </p>
            <p className="text-[13px] leading-[1.75] text-stone-600">
              30秒。声は出していません。
            </p>
          </div>

          <div className="flex flex-col gap-3 border-t border-stone-200 pt-6">
            <span className="text-[11px] tracking-[0.02em] text-stone-400">
              いま使った返し方
            </span>
            <div className="flex flex-col gap-2.5">
              {chosen.map((c, i) => (
                <p key={i} className="text-sm leading-[1.6] text-stone-800">
                  {c.pattern}
                </p>
              ))}
            </div>
            <p className="text-xs leading-[1.7] text-stone-600">
              この{chosen.length}つだけで、雑談はしばらく続きます。
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <p className="text-center text-[11px] text-stone-400">
            無料の回数は減っていません
          </p>
          <button
            onClick={() => {
              logEvent(scenarioId, "to_main");
              navigate(`/talk/${scenarioId}`);
            }}
            className="h-14 rounded-xl bg-stone-800 text-base font-semibold text-white"
          >
            次は、自分の声で話してみる
          </button>
          <button
            onClick={() => navigate("/")}
            className="h-9 text-sm text-stone-600"
          >
            今日はここまでにする
          </button>
        </div>
      </div>
    );
  }

  // ---- 相手の一言を聞く / 読み上げ中 --------------------------------------
  if (phase === "partner" || phase === "reading") {
    const readingChoice = chosen[chosen.length - 1];
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col p-4">
        <Header step={step + (phase === "reading" ? 1 : 0)} total={script.steps.length} />

        <div className="flex flex-1 flex-col justify-center gap-6 px-1">
          {phase === "reading" ? (
            <>
              <div className="flex flex-col gap-1 opacity-45">
                <span className="text-[11px] tracking-[0.02em] text-stone-400">
                  {script.partnerName}
                </span>
                <p className="text-[15px] leading-[1.65] text-stone-800">
                  {current.partner.en}
                </p>
              </div>
              <div className="flex flex-col gap-2 pl-7">
                <Speaker name="あなた" />
                <p className="text-[17px] leading-[1.6] text-stone-600">
                  {readingChoice?.en}
                </p>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <Speaker name={script.partnerName} live={started} />
              <p className="text-[19px] leading-[1.6] text-stone-800">
                {current.partner.en}
              </p>
              <p className="mt-0.5 text-[13px] leading-[1.6] text-stone-500">
                {current.partner.ja}
              </p>
            </div>
          )}
        </div>

        {phase === "reading" ? (
          <p className="pb-1.5 text-center text-xs leading-[1.6] text-stone-400">
            読み上げています
          </p>
        ) : (
          <button
            onClick={() => void listen(step)}
            className="flex h-[52px] items-center justify-center gap-2 rounded-xl border border-stone-300 text-sm text-stone-600"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
            </svg>
            {started ? "もう一度聞く" : "タップして聞く"}
          </button>
        )}
      </div>
    );
  }

  // ---- 返し方を選ぶ --------------------------------------------------------
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col p-4">
      <Header step={step} total={script.steps.length} />

      <div className="flex flex-col gap-1 px-1 pb-5">
        <span className="text-[11px] tracking-[0.02em] text-stone-400">
          {script.partnerName}
        </span>
        <p className="text-[15px] leading-[1.65] text-stone-800">
          {current.partner.en}
        </p>
        <p className="mt-0.5 text-[13px] leading-[1.6] text-stone-500">
          {current.partner.ja}
        </p>
      </div>

      <p className="mb-3 px-1 text-sm font-semibold text-stone-800">
        どう返しますか
      </p>

      <div className="flex flex-col gap-2.5">
        {current.choices.map((c) => (
          <button
            key={c.audioId}
            onClick={() => void pick(c)}
            className="flex flex-col gap-1.5 rounded-xl border border-stone-200 bg-white px-4 py-3.5 text-left"
          >
            {/* 小さいほうが本体。渡したいのは英語表現ではなく会話の型。 */}
            <span className="text-xs text-stone-400">{c.pattern}</span>
            <span className="text-base font-semibold leading-[1.5] text-stone-800">
              {c.en}
            </span>
          </button>
        ))}
      </div>

      <p className="mt-3.5 px-1 text-[11px] leading-[1.6] text-stone-400">
        {step + 1 < script.steps.length
          ? "どれを選んでも先に進みます。間違いはありません。"
          : "これで最後です。"}
      </p>
    </div>
  );
}

function Header({ step, total }: { step: number; total: number }) {
  return (
    <header className="flex items-center justify-between pb-3.5">
      <span className="text-[13px] text-stone-500">今日は聞くだけ</span>
      <div
        className="flex items-center gap-[5px]"
        aria-label={`${total}往復のうち${step}往復`}
      >
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={[
              "size-[5px] rounded-full",
              i < step ? "bg-stone-500" : "bg-stone-300",
            ].join(" ")}
          />
        ))}
      </div>
    </header>
  );
}

function Speaker({ name, live = true }: { name: string; live?: boolean }) {
  return (
    <span className="flex items-center gap-1.5 text-[11px] tracking-[0.02em] text-stone-400">
      {name}
      {live && (
        <span className="flex h-[9px] items-end gap-[2px]" aria-hidden="true">
          {[5, 9, 6].map((h, i) => (
            <span
              key={i}
              className="level-bar w-[2px] rounded-full bg-stone-400"
              style={{ height: `${h}px`, animationDelay: `${i * 90}ms` }}
            />
          ))}
        </span>
      )}
    </span>
  );
}
